import { apiUrl } from "../../config/api";
import React, { useState } from "react";
import axios from "axios";

import "./Contact.css";
import "./Contact.mobile.css";


/* ============================================================
   PROCESS
   ============================================================ */

const PROCESS_STEPS = [
    {
        id: "01",
        title: "BRIEF",
        description:
            "Décrivez votre projet, son contexte, vos objectifs et vos contraintes.",
    },
    {
        id: "02",
        title: "ANALYSE",
        description:
            "J'analyse votre besoin et je définis une stratégie sur mesure.",
    },
    {
        id: "03",
        title: "EXÉCUTION",
        description:
            "Je conçois et développe la solution avec un suivi rigoureux.",
    },
];


/* ============================================================
   INITIAL FORM STATE
   ============================================================ */

const INITIAL_FORM = {
    name: "",
    subject: "",
    phone: "",
    email: "",
    message: "",
};


/* ============================================================
   FORM FIELD
   ============================================================ */

function FormField({
    label,
    id,
    name,
    type = "text",
    value,
    onChange,
    placeholder,
    required = false,
    extraClass = "",
}) {
    return (
        <div className="contact-form__field">

            <label
                htmlFor={id}
                className="contact-form__label"
            >
                {label}

                {required && (
                    <span
                        className="contact-form__label-required"
                        aria-hidden="true"
                    >
                        *
                    </span>
                )}
            </label>

            <input
                id={id}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                className={`contact-form__input ${extraClass}`}
                autoComplete={
                    type === "email"
                        ? "email"
                        : type === "tel"
                            ? "tel"
                            : "off"
                }
            />

        </div>
    );
}


/* ============================================================
   PROCESS LIST
   ============================================================ */

function ProcessList({
    mobile = false
}) {
    return (
        <ol
            className={
                mobile
                    ? "contact-mobile-process__list"
                    : "contact-steps"
            }
            aria-label="Mon processus en trois étapes"
        >

            {PROCESS_STEPS.map((step) => (

                <li
                    key={step.id}
                    className={
                        mobile
                            ? "contact-mobile-process__item"
                            : "contact-step"
                    }
                >

                    <span
                        className={
                            mobile
                                ? "contact-mobile-process__number"
                                : "contact-step__number"
                        }
                        aria-hidden="true"
                    >
                        {step.id}
                    </span>

                    <div
                        className={
                            mobile
                                ? "contact-mobile-process__content"
                                : "contact-step__content"
                        }
                    >

                        <h2
                            className={
                                mobile
                                    ? "contact-mobile-process__title"
                                    : "contact-step__title"
                            }
                        >
                            {step.title}
                        </h2>

                        <p
                            className={
                                mobile
                                    ? "contact-mobile-process__description"
                                    : "contact-step__description"
                            }
                        >
                            {step.description}
                        </p>

                    </div>

                </li>

            ))}

        </ol>
    );
}


/* ============================================================
   CONTACT
   ============================================================ */

function ContactPage() {

    const [formData, setFormData] =
        useState(INITIAL_FORM);

    const [statusType, setStatusType] =
        useState(null);

    const [statusMessage, setStatusMessage] =
        useState("");

    const isSubmitting =
        statusType === "loading";


    /* ========================================================
       CHANGE
       ======================================================== */

    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;

        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };


    /* ========================================================
       SUBMIT
       ======================================================== */

    const handleSubmit = async (event) => {

        event.preventDefault();

        setStatusType("loading");
        setStatusMessage("ENVOI EN COURS…");

        try {

            await axios.post(
                apiUrl("/api/contact/submit_contact_form/"),
                formData
            );

            setStatusType("success");

            setStatusMessage(
                "✓ MESSAGE REÇU — Je vous répondrai sous 24 h."
            );

            setFormData(INITIAL_FORM);

        } catch (error) {

            console.error(
                "Erreur lors de l'envoi :",
                error
            );

            setStatusType("error");

            setStatusMessage(
                "Une erreur est survenue. Veuillez réessayer."
            );

        } finally {

            setTimeout(() => {

                setStatusType(null);
                setStatusMessage("");

            }, 5000);
        }
    };


    return (

        <div className="contact-page">


            <div
                className="contact-page__grid-overlay"
                aria-hidden="true"
            />


            <main className="contact-layout">


                {/* =================================================
                    EDITORIAL
                    ================================================= */}

                <section
                    className="contact-layout__info-panel"
                    aria-labelledby="contact-info-heading"
                >

                    <div className="contact-info">


                        <p className="contact-info__eyebrow">
                            Parlons de votre projet
                        </p>


                        <h1
                            id="contact-info-heading"
                            className="contact-info__heading"
                        >
                            LET'S TALK
                        </h1>


                        <p className="contact-info__intro">
                            Je suis à l'écoute de vos projets,
                            de vos idées et de vos ambitions.
                            Question ponctuelle ou projet complexe :
                            je vous aide à clarifier, structurer
                            et concrétiser votre besoin.
                        </p>


                        <ProcessList />


                        <div
                            className="contact-badges"
                            aria-label="Mes engagements"
                        >

                            <p className="contact-badge">
                                <span aria-hidden="true">
                                    ⚡
                                </span>

                                Je réponds en moins de 24 h
                            </p>


                            <p className="contact-badge">
                                <span aria-hidden="true">
                                    🔒
                                </span>

                                Confidentialité & données sécurisées
                            </p>

                        </div>

                    </div>

                </section>


                {/* =================================================
                    FORM
                    ================================================= */}

                <section
                    className="contact-layout__form-panel"
                    aria-labelledby="contact-form-heading"
                >

                    <div className="contact-form-card">


                        <header className="contact-form__header">

                            <span className="contact-form__eyebrow">
                                VOTRE PROJET
                            </span>


                            <h2
                                id="contact-form-heading"
                                className="contact-form__title"
                            >
                                CONTACTEZ-MOI
                            </h2>


                            <p className="contact-form__subtitle">
                                Les champs marqués{" "}
                                <span aria-label="obligatoire">
                                    *
                                </span>{" "}
                                sont requis
                            </p>

                        </header>


                        <form
                            onSubmit={handleSubmit}
                            className="contact-form"
                            noValidate
                        >


                            <FormField
                                label="Nom complet"
                                id="field-name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Ex : Marie Dupont"
                                required
                            />


                            <div className="contact-form__field-row">

                                <FormField
                                    label="Sujet"
                                    id="field-subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    placeholder="Ex : Refonte du site"
                                    required
                                />


                                <FormField
                                    label="Téléphone"
                                    id="field-phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="+33 6 12 34 56 78"
                                    required
                                />

                            </div>


                            <FormField
                                label="Adresse e-mail"
                                id="field-email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Ex : marie@example.com"
                                required
                                extraClass="contact-form__input--email"
                            />


                            <div className="contact-form__field">

                                <label
                                    htmlFor="field-message"
                                    className="contact-form__label"
                                >
                                    Votre message

                                    <span
                                        className="contact-form__label-required"
                                        aria-hidden="true"
                                    >
                                        *
                                    </span>
                                </label>


                                <textarea
                                    id="field-message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Décrivez votre projet, vos besoins, votre calendrier…"
                                    rows={5}
                                    required
                                    className="contact-form__textarea"
                                />

                            </div>


                            <div className="contact-form__footer">

                                <p className="contact-form__disclaimer">
                                    Vos données ne seront jamais
                                    partagées avec des tiers.
                                </p>


                                <button
                                    type="submit"
                                    className="contact-form__submit-btn"
                                    disabled={isSubmitting}
                                    aria-busy={isSubmitting}
                                >
                                    {isSubmitting
                                        ? "ENVOI…"
                                        : "ENVOYER LE MESSAGE →"
                                    }
                                </button>

                            </div>


                            {statusMessage && (

                                <p
                                    role="status"
                                    aria-live="polite"
                                    className={
                                        `contact-form__status contact-form__status--${statusType}`
                                    }
                                >
                                    {statusMessage}
                                </p>

                            )}

                        </form>

                    </div>

                </section>


                {/* =================================================
                    MOBILE PROCESS
                    ================================================= */}

                <section
                    className="contact-mobile-process"
                    aria-labelledby="contact-mobile-process-title"
                >

                    <header className="contact-mobile-process__header">

                        <span className="contact-mobile-process__eyebrow">
                            MÉTHODE
                        </span>

                        <h2 id="contact-mobile-process-title">
                            DE L'IDÉE À L'EXÉCUTION
                        </h2>

                    </header>


                    <ProcessList mobile />

                </section>


            </main>

        </div>
    );
}


export default ContactPage;